import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Button } from '@components/Button';
import { useAuth } from '@hooks/useAuth';
import { BikeStatus, BikeType } from '@shared/types/bike';
import { formatCurrency } from '@lib/utils';
import { ROUTES } from '@lib/routes';

// Mock data - will be replaced with Firestore query
const mockBike = {
  id: '1',
  name: 'City Cruiser Pro',
  description:
    'Perfect for urban commuting and leisurely rides. This e-bike features a powerful motor, long-lasting battery, and comfortable design.',
  type: BikeType.CITY,
  status: BikeStatus.AVAILABLE,
  halfDayRate: 2500, // $25.00
  fullDayRate: 4000, // $40.00
  twoDayDiscount: 10,
  threeDayDiscount: 15,
  weekDiscount: 20,
  batteryLevel: 85,
  mileage: 1250,
  imageUrls: [
    'https://via.placeholder.com/800x600?text=Bike+1',
    'https://via.placeholder.com/800x600?text=Bike+1+Side',
    'https://via.placeholder.com/800x600?text=Bike+1+Detail',
  ],
  specifications: {
    maxSpeed: 25,
    range: 60,
    weight: 22,
    maxLoad: 120,
  },
  locationId: 'loc1',
  createdAt: new Date(),
  updatedAt: new Date(),
};

export function BikeDetailPage(): JSX.Element {
  const { bikeId: _bikeId } = useParams<{ bikeId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [bike] = useState(mockBike); // Will be replaced with useQuery

  const handleBookNow = (): void => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN, {
        state: { from: ROUTES.CUSTOMER.BOOKING, bikeId: bike.id },
      });
      return;
    }
    navigate(ROUTES.CUSTOMER.BOOKING, {
      state: { bikeId: bike.id },
    });
  };

  if (!bike) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Bike not found
              </p>
              <Link to={ROUTES.CUSTOMER.SEARCH}>
                <Button variant="outline" className="mt-4">
                  Back to Search
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <Link
          to={ROUTES.CUSTOMER.SEARCH}
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 mb-4 inline-block"
        >
          ← Back to Search
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Images */}
          <div>
            <Card padding="none" className="overflow-hidden mb-4">
              <img
                src={bike.imageUrls[selectedImageIndex] || bike.imageUrls[0]}
                alt={bike.name}
                className="w-full h-96 object-cover"
              />
            </Card>
            {bike.imageUrls.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {bike.imageUrls.map((url, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`border-2 rounded-lg overflow-hidden ${
                      selectedImageIndex === index
                        ? 'border-blue-600 dark:border-blue-400'
                        : 'border-transparent'
                    }`}
                  >
                    <img
                      src={url}
                      alt={`${bike.name} view ${index + 1}`}
                      className="w-full h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {bike.name}
            </h1>

            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm font-medium capitalize">
                {bike.type}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  bike.status === BikeStatus.AVAILABLE
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {bike.status}
              </span>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(bike.halfDayRate)}
                </span>
                <span className="text-gray-600 dark:text-gray-400">/half-day</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(bike.fullDayRate)}
                </span>
                <span className="text-gray-600 dark:text-gray-400">/full-day</span>
              </div>
              {(bike.twoDayDiscount || bike.threeDayDiscount || bike.weekDiscount) && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Extended Rental Discounts:
                  </p>
                  <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {bike.twoDayDiscount && (
                      <p>2-day rental: {bike.twoDayDiscount}% off</p>
                    )}
                    {bike.threeDayDiscount && (
                      <p>3-day rental: {bike.threeDayDiscount}% off</p>
                    )}
                    {bike.weekDiscount && (
                      <p>Week rental (7 days): {bike.weekDiscount}% off</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Card className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Description
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{bike.description}</p>
            </Card>

            {bike.specifications && (
              <Card className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Specifications
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {bike.specifications.maxSpeed && (
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Max Speed
                      </span>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {bike.specifications.maxSpeed} km/h
                      </p>
                    </div>
                  )}
                  {bike.specifications.range && (
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Range</span>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {bike.specifications.range} km
                      </p>
                    </div>
                  )}
                  {bike.specifications.weight && (
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Weight</span>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {bike.specifications.weight} kg
                      </p>
                    </div>
                  )}
                  {bike.specifications.maxLoad && (
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Max Load
                      </span>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {bike.specifications.maxLoad} kg
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {bike.batteryLevel !== undefined && (
              <Card className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Battery Level
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {bike.batteryLevel}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${bike.batteryLevel}%` }}
                  />
                </div>
              </Card>
            )}

            <Button
              fullWidth
              size="lg"
              onClick={handleBookNow}
              disabled={bike.status !== BikeStatus.AVAILABLE}
            >
              {bike.status === BikeStatus.AVAILABLE ? 'Book Now' : 'Not Available'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

